import { calculateRoundedGstAmount } from '../../../utils/gstCalculation';
import {
  isNilTaxability,
  NIL_TAXABILITY_VALUE,
} from '../Components/UseTaxabilityOptions';

export { isNilTaxability, NIL_TAXABILITY_VALUE };

export type ItemPriceGstRate = {
  id: number;
  rate: string;
};

export const NIL_GST_RATE: ItemPriceGstRate = { id: -1, rate: 'NIL' };

export type ItemPriceTableType = 'Sales' | 'Purchase';

export type ItemPriceTaxabilityRow = {
  amount?: number;
  DiscountType?: number;
  DiscountValue?: number;
  cesstype?: number;
  cess?: number;
  gstRate?: ItemPriceGstRate;
  discountAmt?: number;
  amtaftdicount?: number;
  amountIncludingGst?: number;
};

export type CalculateItemPriceGstAndCessParams = {
  amountAfterDiscount: number;
  taxabilityValue: number;
  tableType?: ItemPriceTableType;
  gstRateId?: number;
  cesstype?: number;
  cess?: number;
  /** Match legacy half-cent GST rounding used in price table onChange handlers. */
  applyHalfRound?: boolean;
};

export type ItemPriceGstAndCessResult = {
  gstAmount: number;
  cessAmount: number;
};

/**
 * Item Master price tables: exclude GST and Cess from net when
 * Exempted / Non-Taxable / Out of scope / NIL, or RCM on Sales.
 */
export const shouldExcludeGstAndCessFromItemNet = (
  taxabilityValue: number,
  tableType?: ItemPriceTableType
): boolean => {
  if (
    taxabilityValue === 2 ||
    taxabilityValue === 3 ||
    taxabilityValue === 5 ||
    taxabilityValue === NIL_TAXABILITY_VALUE ||
    isNilTaxability(taxabilityValue)
  ) {
    return true;
  }
  if (taxabilityValue === 4 && tableType === 'Sales') {
    return true;
  }
  return false;
};

const applyLegacyHalfRoundGst = (gstAmount: number): number => {
  let amount = gstAmount / 2;
  amount = Math.round(amount * 100) / 100;
  return amount * 2;
};

/**
 * Shared GST + Cess calculation for Item Selling/Purchase price tables.
 * For NIL (and other zero-tax taxabilities), both amounts are 0.
 */
export const calculateItemPriceGstAndCess = ({
  amountAfterDiscount,
  taxabilityValue,
  tableType,
  gstRateId,
  cesstype,
  cess,
  applyHalfRound = true,
}: CalculateItemPriceGstAndCessParams): ItemPriceGstAndCessResult => {
  if (shouldExcludeGstAndCessFromItemNet(taxabilityValue, tableType)) {
    return { gstAmount: 0, cessAmount: 0 };
  }

  const taxable = amountAfterDiscount;
  const cessRate = cess ?? 0;
  const cessAmount = cesstype === 1 ? (taxable * cessRate) / 100 : cessRate;


  let gstAmount = 0;
  if (gstRateId !== undefined && gstRateId > 0) {
    gstAmount =calculateRoundedGstAmount(taxable, gstRateId);
  }

  return { gstAmount, cessAmount };
};

/**
 * When Taxability becomes NIL: lock GST Rate to NIL, clear Cess to 0,
 * and set net = taxable only (GST = 0, Cess = 0 in total).
 */
export const recalculateItemPriceRowForNil = <T extends ItemPriceTaxabilityRow>(
  row: T
): T => {
  const amount = row.amount ?? 0;
  const discountValue = row.DiscountValue ?? 0;
  const discountAmt =
    row.DiscountType === 1 ? (amount * discountValue) / 100 : discountValue;
  const amountAfterDiscount = amount - discountAmt;

  return {
    ...row,
    gstRate: NIL_GST_RATE,
    cess: 0,
    discountAmt,
    amtaftdicount: amountAfterDiscount,
    amountIncludingGst: amountAfterDiscount,
  };
};
